import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "handle-url-clicks-from-an-in-app-message"
    }}>{`Handle URL clicks from an in-app message`}</h1>
    <p>{`When a link is clicked in an in-app message, the `}<inlineCode parentName="p">{`FullscreenMessageDelegate`}</inlineCode>{` is responsible for handling behavior.`}</p>
    <p>{`If the scheme used in the link `}<strong parentName="p">{`is not`}</strong>{` `}<inlineCode parentName="p">{`adbinapp://`}</inlineCode>{`, the link will open in the containing WKWebView (iOS) or WebView (Android) - this is default behavior.`}</p>
    <p>{`If the scheme used in the link `}<strong parentName="p">{`is`}</strong>{` `}<inlineCode parentName="p">{`adbinapp://`}</inlineCode>{`, the following behavior may occur:`}</p>
    <ul>
      <li parentName="ul">{`The in-app message is dismissed`}</li>
      <li parentName="ul">{`An interaction event is sent to Experience Edge`}</li>
      <li parentName="ul">{`The default animation is overridden`}</li>
      <li parentName="ul">{`The URL will be opened by the OS, potentially resulting in:`}
        <ul parentName="li">
          <li parentName="ul">{`The link being opened by the device's default web browser`}</li>
          <li parentName="ul">{`Opening of a deeplink`}</li>
        </ul>
      </li>
    </ul>
    <h2 {...{
      "id": "dismiss-the-in-app-message"
    }}>{`Dismiss the in-app message`}</h2>
    <p>{`In order for the SDK to remove the view containing an in-app message from the UI, the clicked link must have a host of `}<inlineCode parentName="p">{`dismiss`}</inlineCode>{`.`}</p>
    <p>{`The example below is a link that will dismiss the current in-app message:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`adbinapp://dismiss
`}</code></pre>
    <h2 {...{
      "id": "send-a-message-interaction-event-to-experience-edge"
    }}>{`Send a message interaction event to Experience Edge`}</h2>
    <p>{`Adding a URL variable named `}<inlineCode parentName="p">{`interaction`}</inlineCode>{` will cause the SDK to send an Experience Event to the Adobe Experience Edge.`}</p>
    <p>{`The example below will dismiss the current in-app message and send a `}<inlineCode parentName="p">{`decisioning.propositionInteract`}</inlineCode>{` event to edge with an action of `}<inlineCode parentName="p">{`imageLiked`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`adbinapp://dismiss?interaction=imageLiked
`}</code></pre>
    <h2 {...{
      "id": "override-the-default-dismiss-animation"
    }}>{`Override the default dismiss animation`}</h2>
    <p>{`Adding a URL variable named `}<inlineCode parentName="p">{`animate`}</inlineCode>{` will cause the SDK to override the dismiss animation for the message.`}</p>
    <p>{`The example below will dismiss the current in-app message, and override the animation so the message exits to the right side of the screen:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`adbinapp://dismiss?animate=right
`}</code></pre>
    <p>{`Below is a list of valid values for `}<inlineCode parentName="p">{`animate`}</inlineCode>{`:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`none`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Message is removed with no animation`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`left`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Message animates off the screen to the `}<strong parentName="td">{`left`}</strong>{` when dismissed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`right`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Message animates off the screen to the `}<strong parentName="td">{`right`}</strong>{` when dismissed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`top`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Message animates off the screen to the `}<strong parentName="td">{`top`}</strong>{` when dismissed`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`bottom`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Message animates off the screen to the `}<strong parentName="td">{`bottom`}</strong>{` when dismissed`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`If the value for `}<inlineCode parentName="p">{`animate`}</inlineCode>{` is empty, or if it doesn't match one of the above valid values, an animation of `}<inlineCode parentName="p">{`none`}</inlineCode>{` will be used.`}</p>
    <h2 {...{
      "id": "open-a-link-from-the-url"
    }}>{`Open a link from the URL`}</h2>
    <p>{`If the value for a URL variable named `}<inlineCode parentName="p">{`link`}</inlineCode>{` contains a valid URL, the SDK will open the link using the OS-defined API.`}</p>
    <h3 {...{
      "id": "open-location-in-the-default-web-browser"
    }}>{`Open location in the default web browser`}</h3>
    <p>{`If the provided URL does not contain a custom scheme, the URL will be loaded in the device's default web browser.  `}</p>
    <p>{`The example below will dismiss the current in-app message, send a `}<inlineCode parentName="p">{`decisioning.propositionInteract`}</inlineCode>{` event to edge with an action of `}<inlineCode parentName="p">{`adobe`}</inlineCode>{`, and open the adobe.com website in the default web browser for the user's device:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`adbinapp://dismiss?interaction=adobe&link=https%3A%2F%2Fwww.adobe.com%3Fkey%3Dvalue
`}</code></pre>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`The `}<inlineCode parentName="p">{`link`}</inlineCode>{` parameter must be URL encoded. If you are setting this value in the AJO message authoring UI, the value will automatically be encoded. If your message is custom HTML, you must encode the value manually.`}</p>
    <h3 {...{
      "id": "open-a-deeplink"
    }}>{`Open a deeplink`}</h3>
    <InlineAlert variant="help" slots="text" mdxType="InlineAlert" />
    <p>{`Android deeplinks require intent filters to be added in the app manifest to correctly handle deeplink URLs. Please see the `}<a parentName="p" {...{
        "href": "https://developer.android.com/training/app-links/deep-linking"
      }}>{`Android developer documentation`}</a>{` for more information.`}</p>
    <InlineAlert variant="help" slots="text" mdxType="InlineAlert" />
    <p>{`iOS deeplinks require a custom URL scheme to be setup in the application's `}<inlineCode parentName="p">{`info`}</inlineCode>{` tab within Xcode to correctly handle deeplink URLs. Please see the `}<a parentName="p" {...{
        "href": "https://developer.apple.com/documentation/xcode/defining-a-custom-url-scheme-for-your-app"
      }}>{`Apple developer documentation`}</a>{` for more information.`}</p>
    <p>{`If the provided URL contains a custom scheme, the app that handles the custom scheme will be launched.`}</p>
    <p>{`The example below will dismiss the current in-app message, then launch an app owned by the same developer which handles the scheme `}<inlineCode parentName="p">{`myAppScheme`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-shell"
      }}>{`adbinapp://dismiss?link=myAppScheme://deeplinked
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      